import React, { useState, useEffect, useCallback } from 'react';
import Tabs from '@material-ui/core/Tabs';
import { useQuery } from '@apollo/client';
import { useDispatch, useSelector } from 'react-redux';
import _ from 'lodash';
import PropTypes from 'prop-types';

import { providerPageWidth } from '../../constant';

import { ProviderTab, Text } from '../../components/index';
import CreditsBalanceCard from '../../components/Provider/Credits/CreditsBalanceCard';
import InvoiceCard from '../../components/Provider/Credits/InvoiceCard';
import CreditsList from '../../components/Provider/Credits/CreditsListCard';

import {
    GET_CREDIT,
    GET_TOP_UPS,
    GET_PENDING,
    GET_TRANSACTION,
    cancelTopUp,
} from '../../services/provider';
import { alertError, alertSuccess } from '../../redux/stateActions';
import socket from '../../socket';

const Credits = ({ history }) => {
    const dispatch = useDispatch();
    const user = useSelector((state) => state.user);

    const [selectedTab, setSelectedTab] = useState(0);

    const tabsChangeHandler = (event, newValue) => {
        setSelectedTab(newValue);
    };

    /*--------------- Get Credit ---------------*/
    const [creditsBalance, setCreditsBalance] = useState({ credits: 0, ref: '', updatedAt: null });
    const {
        loading: getCreditLoading,
        error: getCreditError,
        data: getCreditData,
        refetch: getCreditRefetch,
    } = useQuery(GET_CREDIT, {
        variables: { id: user.staff.hospital.id },
        skip: _.isEmpty(user),
    });

    useEffect(() => {
        if (getCreditError) {
            dispatch(alertError(`Cannot get Credit : ${getCreditError.message}`));
        }

        if (getCreditData && !_.isEmpty(getCreditData)) {
            setCreditsBalance({
                credits: getCreditData.hospital.credit,
                ref: '',
                updatedAt: !_.isEmpty(getCreditData.topUps)
                    ? getCreditData.topUps[0].updatedAt
                    : null,
            });
        }
    }, [getCreditData, getCreditError]);

    /*--------------- Get Transactions ---------------*/
    const [transaction, setTransaction] = useState([]);
    const [limitTransaction, setLimitTransaction] = useState(10);
    const [canLoadMoreTransaction, setCanLoadMoreTransaction] = useState(true);

    // use 'start' instead of 'limit' will cause item to duplicate somehow
    // but does not happend in graphQL playground
    const {
        loading: getTransactionLoading,
        error: getTransactionError,
        data: getTransactionData,
        refetch: getTransactionRefetch,
    } = useQuery(GET_TRANSACTION, {
        variables: {
            id: user.staff.hospital.id,
            limit: limitTransaction,
            // start: startTransaction,
        },
        skip: _.isEmpty(user),
    });

    useEffect(() => {
        if (getTransactionError) {
            dispatch(alertError(`Cannot Get Usage : ${getTransactionError.message}`));
        }

        if (getTransactionData) {
            setTransaction(getTransactionData.transactions);

            if (
                getTransactionData.transactions.length >=
                getTransactionData.transactionsConnection.aggregate.count
            ) {
                setCanLoadMoreTransaction(false);
            }
        }
    }, [getTransactionData, getTransactionError]);

    const handleLoadMoreTransaction = useCallback(() => {
        const restOfTransaction =
            getTransactionData.transactionsConnection.aggregate.count - transaction.length;

        if (restOfTransaction < 10) {
            setLimitTransaction(limitTransaction + restOfTransaction);
        } else {
            setLimitTransaction(limitTransaction + 10);
        }
    }, [transaction, getTransactionData, limitTransaction]);

    /*--------------- Get TopUps ---------------*/
    const [topUpData, setTopUpData] = useState([]);
    const [limitTopUps, setLimitTopUps] = useState(10);
    const [canLoadMoreTopUps, setCanLoadMoreTopUps] = useState(true);

    const {
        loading: getTopUpsLoading,
        error: getTopUpsError,
        data: getTopUpsData,
        refetch: getTopUpsRefetch,
    } = useQuery(GET_TOP_UPS, {
        variables: { id: user.staff.hospital.id, limit: limitTopUps },
        skip: _.isEmpty(user),
    });

    useEffect(() => {
        if (getTopUpsError) {
            dispatch(alertError(`Cannot get Payment History : ${getTopUpsError.message}`));
        }

        if (getTopUpsData && getTopUpsData.topUps) {
            setTopUpData(getTopUpsData.topUps);

            if (getTopUpsData.topUps.length >= getTopUpsData.topUpsConnection.aggregate.count) {
                setCanLoadMoreTopUps(false);
            }
        }
    }, [getTopUpsData, getTopUpsError]);

    const handleLoadMoreTopUps = useCallback(() => {
        const restOfTopUps = getTopUpsData.topUpsConnection.aggregate.count - topUpData.length;

        if (restOfTopUps < 10) {
            setLimitTopUps(limitTopUps + restOfTopUps);
        } else {
            setLimitTopUps(limitTopUps + 10);
        }
    }, [getTopUpsData, topUpData, limitTopUps]);

    useEffect(() => {
        socket.on('updateCredit', () => {
            getCreditRefetch();
        });
        socket.on('newTransaction', () => {
            getCreditRefetch();
            getTransactionRefetch();
        });
        socket.on('topUpUpdated', () => {
            getCreditRefetch();
            getTopUpsRefetch();
            getPendingRefetch();
            getTransactionRefetch();
        });
    }, []);

    /*--------------- Get Pending ---------------*/
    const [invoiceList, setInvoiceList] = useState([]);

    const {
        error: getPendingError,
        data: getPendingData,
        refetch: getPendingRefetch,
    } = useQuery(GET_PENDING, {
        variables: { id: user.staff.hospital.id },
        skip: _.isEmpty(user),
    });

    useEffect(() => {
        if (!_.isEmpty(user)) {
            getPendingRefetch();
        }
    }, []);

    useEffect(() => {
        if (getPendingError) {
            dispatch(alertError(`Cannot get Credit : ${getPendingError.message}`));
        }

        if (getPendingData) {
            setInvoiceList(getPendingData.topUps);
        }
    }, [getPendingData, getPendingError]);

    const handleCheckout = () => {
        history.push({
            pathname: '/provider/credits/checkout',
        });
    };

    const handleOnClick = (id) => {
        window.open(`/provider/invoice/${id}`, '_blank');
    };
    const handleOnCancel = async (id) => {
        try {
            const res = await cancelTopUp({ id });

            if (res.status === 200) {
                dispatch(alertSuccess(`Invoice Cancelled`));
                getTopUpsRefetch();
                getPendingRefetch();
            } else {
                throw new Error(res);
            }
        } catch (error) {
            dispatch(alertError(error.response?.data?.message));
        }
    };

    return (
        <div
            style={{
                padding: '41px 20px 50px',
                maxWidth: providerPageWidth,
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                rowGap: '25px',
            }}
        >
            <Text $fontSize="24px" $bold>
                Credit
            </Text>
            <CreditsBalanceCard
                data={creditsBalance}
                onClick={handleCheckout}
                loading={getCreditLoading}
            />

            <Tabs
                value={selectedTab}
                onChange={tabsChangeHandler}
                TabIndicatorProps={{ style: { background: 'transparent' } }}
                variant="standard"
            >
                <ProviderTab label="Credits" $active={selectedTab === 0} />
                <ProviderTab label="Payment History" $active={selectedTab === 1} />
            </Tabs>

            <div
                style={{
                    display: 'flex',
                    flexDirection: 'column',
                    rowGap: '25px',
                }}
            >
                {!_.isEmpty(invoiceList) && (
                    <InvoiceCard
                        data={invoiceList}
                        refetch={getPendingRefetch}
                        handleOnClick={handleOnClick}
                        handleOnCancel={handleOnCancel}
                    />
                )}

                {selectedTab === 0 && (
                    <CreditsList
                        type="usage"
                        data={transaction}
                        loading={getTransactionLoading}
                        onLoadMore={handleLoadMoreTransaction}
                        canLoadMore={canLoadMoreTransaction}
                    />
                )}
                {selectedTab === 1 && (
                    <CreditsList
                        type="reciept"
                        data={topUpData}
                        loading={getTopUpsLoading}
                        onLoadMore={handleLoadMoreTopUps}
                        canLoadMore={canLoadMoreTopUps}
                    />
                )}
            </div>
        </div>
    );
};

Credits.propTypes = {
    history: PropTypes.object,
};

export default Credits;
